import qs from 'qs'
import { useMemo } from 'react'
import useSWR from 'swr'

import { FALLBACK_LANGUAGE } from '../../constants/config'
import { useConfig } from '../../context/config'
import fetcher from '../../lib/fetcher'
import { makeStrapiImageUrl } from '../../util/url'
import { OrderProduct } from '../use-patient-current-consultation/types'

import { Product, ProductGroup, StrapiProduct, StrapiSymptomCollectorQuestion, SymptomCollectorQuestion } from './types'

const symptomTypeMap = {
  'question.mcq': 'multipleChoice',
  'question.slider': 'slider',
  'question.text': 'text',
  'question.yes-no': 'yesNo',
  'question.radio': 'radio',
} as const

export function transform({ attributes, ...rest }: StrapiProduct): Product {
  const { icon, symptomCollector, workingHours, category, preSymptomCollectorPicture, ...restAttrs } = attributes
  return {
    ...rest,
    ...restAttrs,
    icon: transformIcon(icon),
    symptomCollector: symptomCollector ? transformSymptomCollector(symptomCollector) : null,
    workingHours: transformWorkingHours(workingHours),
    category: transformCategory(category),
    preSymptomCollectorPicture: preSymptomCollectorPicture
      ? transformPreSymptomCollectorPicture(preSymptomCollectorPicture)
      : null,
  }
}

export function transformIcon(icon: StrapiProduct['attributes']['icon']): Product['icon'] {
  return icon.data ? { id: icon.data.id, ...icon.data.attributes } : null
}

export function transformSymptomCollector(
  symptomCollector: StrapiSymptomCollectorQuestion[]
): SymptomCollectorQuestion[] {
  return symptomCollector.map(({ __component, ...q }) => ({
    ...q,
    type: symptomTypeMap[__component],
    id: `${symptomTypeMap[__component]}-${q.id}`,
  })) as SymptomCollectorQuestion[]
}

export function transformWorkingHours(
  workingHours: StrapiProduct['attributes']['workingHours']
): Product['workingHours'] {
  return workingHours.data.map(({ attributes }) => attributes)
}

export function transformCategory(category: StrapiProduct['attributes']['category']): Product['category'] {
  return category.data
}

export function transformPreSymptomCollectorPicture(
  picture: StrapiProduct['attributes']['preSymptomCollectorPicture']
): Product['preSymptomCollectorPicture'] {
  return picture.data ? { id: picture.data.id, url: picture.data.attributes.url } : null
}

interface UseProductSearchOptions {
  slug?: string
}

export function useProducts(search?: UseProductSearchOptions) {
  const isSlugSearch = !!search?.slug
  const { config } = useConfig()
  const query = qs.stringify(
    {
      // TODO: Strapi default pageSize is 25, but the UI right now is designed to show all products at once, we can
      //       safely set this to 100 until we have a better solution
      pagination: { page: 1, pageSize: 100 },
      populate: {
        workingHours: { fields: 'day,closed,startTime,endTime' },
        icon: { fields: 'url' },
        category: { fields: 'id' },
        // Only fetch symptom collector data if we're searching for a specific product from the product screen
        ...(isSlugSearch
          ? {
              symptomCollector: { populate: '*' },
              preSymptomCollectorPicture: { fields: 'url' },
              disclaimer: { populate: '*' },
            }
          : {}),
      },
      ...(isSlugSearch ? {} : { sort: 'order:asc' }),
      localeFallback: config.patientAppLanguage || FALLBACK_LANGUAGE,
      filters: {
        active: 1,
        category: { id: { $notNull: 1 } },
        ...(isSlugSearch ? { slug: { $contains: search.slug } } : {}),
      },
    },
    { encodeValuesOnly: true }
  )

  const { data, error } = useSWR<{ data: StrapiProduct[] }>(`/api/products?${query}`, (url: string) =>
    fetcher.strapi(url, config.patientAppLanguage)
  )
  const transformedProducts = useMemo(() => (data ? data.data.map(transform) : []), [data])

  return {
    products: transformedProducts,
    isLoading: !error && !data,
    error,
  }
}

export function makeProductImageURL(product: Product | ProductGroup | OrderProduct) {
  if (!product?.icon?.url) return undefined

  return makeStrapiImageUrl(product.icon.url)
}
